/* Smart Feed Cleaner dialog: AI-powered bulk feed cleanup with review & confirm. */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Trash2, ArrowRight, Check } from "lucide-react";
import { useFeedCleaner } from "@/hooks/useFeedCleaner";
import {
  CleanerGroupPills,
  CleanerGroupSidebar,
} from "@/components/feed-cleaner/CleanerGroupSidebar";
import { CleanerItemRow } from "@/components/feed-cleaner/CleanerItemRow";

const PRESET_CHIPS = [
  "Keep programming & tech, remove entertainment",
  "Keep educational content, remove news & politics",
  "Keep short videos under 15 min, remove long ones",
  "Remove everything older than 2 weeks",
];

interface FeedCleanerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedCleanerDialog({ open, onOpenChange }: FeedCleanerDialogProps) {
  const [criteria, setCriteria] = useState("");
  const [appliedPreset, setAppliedPreset] = useState<string | null>(null);
  const appliedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cleaner = useFeedCleaner();

  // Clear the "applied" flash after a short delay
  useEffect(() => {
    return () => {
      if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current);
    };
  }, []);

  function handlePresetClick(preset: string) {
    setCriteria(preset);
    setAppliedPreset(preset);
    if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current);
    appliedTimerRef.current = setTimeout(() => setAppliedPreset(null), 1200);
  }

  const activeGroupData = cleaner.result?.groups.find(
    (g) => g.name === cleaner.activeGroup,
  );

  function handleClose(nextOpen: boolean) {
    if (!nextOpen) {
      cleaner.reset();
      setCriteria("");
      setAppliedPreset(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleAnalyze() {
    if (!criteria.trim()) return;
    await cleaner.analyze(criteria.trim());
  }

  async function handleApply() {
    await cleaner.apply();
    handleClose(false);
  }

  const isInputPhase = !cleaner.result && !cleaner.isAnalyzing;
  const isReviewPhase = cleaner.result && !cleaner.isApplying;

  const groupNavProps = cleaner.result
    ? {
        groups: cleaner.result.groups,
        activeGroup: cleaner.activeGroup,
        onSelectGroup: cleaner.setActiveGroup,
        getItemAction: cleaner.getItemAction,
      }
    : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5" />
            Smart Feed Cleaner
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {isInputPhase && "Describe what to keep or remove from your feed"}
            {cleaner.isAnalyzing && "Analyzing your feed..."}
            {isReviewPhase &&
              cleaner.result != null &&
              `${cleaner.result.totalAnalyzed} items analyzed — review and confirm`}
            {cleaner.isApplying && "Applying changes..."}
          </p>
        </DialogHeader>

        {/* Phase 1: Input */}
        {isInputPhase && (
          <div className="flex-1 flex flex-col gap-4 overflow-y-auto p-1">
            <Textarea
              placeholder="e.g. Keep programming and tech content, remove entertainment and gaming"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              className="min-h-[100px] resize-none"
              autoFocus
            />

            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Quick presets</p>
              <div className="flex flex-wrap gap-2">
                {PRESET_CHIPS.map((preset) => {
                  const isApplied = appliedPreset === preset;
                  return (
                    <button
                      key={preset}
                      className={`relative text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-all duration-200 ${
                        isApplied
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent hover:bg-accent hover:border-border text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => handlePresetClick(preset)}
                    >
                      <span className={isApplied ? "invisible" : ""}>{preset}</span>
                      {isApplied && (
                        <span className="absolute inset-0 flex items-center justify-center gap-1">
                          <Check className="size-3" />
                          Applied
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {cleaner.error && (
              <p className="text-sm text-destructive">{cleaner.error}</p>
            )}
          </div>
        )}

        {/* Loading state */}
        {cleaner.isAnalyzing && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Loader2 className="size-8 animate-spin text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">
                AI is categorizing your feed items...
              </p>
            </div>
          </div>
        )}

        {/* Phase 2: Review */}
        {isReviewPhase && groupNavProps && (
          <>
            {/* Mobile: horizontal group pills */}
            <CleanerGroupPills {...groupNavProps} />

            <div className="flex flex-1 min-h-0 overflow-hidden -mx-4 border-t border-border">
              {/* Desktop: sidebar */}
              <CleanerGroupSidebar {...groupNavProps} />

              {/* Items list */}
              <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                {activeGroupData && (
                  <div className="px-3 py-2 border-b border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">
                        {activeGroupData.name}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {activeGroupData.items.length} items
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {activeGroupData.reason}
                    </p>
                  </div>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className="p-2 space-y-1.5">
                    {activeGroupData?.items.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-8">
                        No items in this group
                      </p>
                    ) : (
                      activeGroupData?.items.map((item) => (
                        <CleanerItemRow
                          key={item.id}
                          item={item}
                          isQueued={cleaner.getItemAction(item.id) === "queue"}
                          onToggle={() => cleaner.toggleItem(item.id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Applying state */}
        {cleaner.isApplying && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Loader2 className="size-8 animate-spin text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">Applying changes...</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter>
          {isInputPhase && (
            <Button onClick={handleAnalyze} disabled={!criteria.trim()}>
              <Sparkles className="size-4 mr-1.5" />
              Analyze Feed
            </Button>
          )}

          {isReviewPhase && (
            <>
              <span className="text-xs text-muted-foreground sm:mr-auto flex items-center gap-3">
                <span className="text-primary font-medium">
                  {cleaner.queueCount} keep
                </span>
                <span>
                  <Trash2 className="size-3 inline mr-1" />
                  {cleaner.dismissCount} remove
                </span>
              </span>
              <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleApply}>
                <ArrowRight className="size-4 mr-1.5" />
                Apply
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
